/**
 * Heatmap colour scale — ABSOLUTE thresholds (owner decision, recoloured 2026-06-13):
 *   grey = unlikely · blue = maybe · green = most likely.
 * A time's colour reflects its own confidence score on a fixed 0–100 scale, NOT its rank
 * within the current view. So a wide early search honestly reads mostly grey/blue and only
 * turns green once the evidence is genuinely strong — the visual twin of PLAN.md's
 * precision-gate honesty (never imply a trustworthy answer the evidence doesn't support).
 *
 * Engine scores are directional metrics centred on 50 (neutral = "no signal"); meaningfully
 * supported times sit well above 50, so the stops are anchored accordingly:
 *   ≤45 grey (unlikely) · 62 blue (maybe) · ≥80 green (most likely).
 * "No evidence yet" (null score) is a distinctly LIGHTER grey so it never reads as a verdict.
 */

import { addDays, formatISODate, parseISODate } from "../lib/datetime";

export interface ColorStop {
  score: number;
  rgb: [number, number, number];
}

const STOPS: ColorStop[] = [
  { score: 45, rgb: [108, 112, 122] }, // grey  — unlikely
  { score: 62, rgb: [43, 95, 168] }, // blue  — maybe
  { score: 80, rgb: [34, 161, 74] }, // green — most likely
];

const NEUTRAL: [number, number, number] = [176, 179, 186]; // light grey — no evidence yet

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

/** Absolute score (0–100) → CSS rgb() colour. `null`/undefined → neutral grey (no evidence). */
export function scoreToColor(score: number | null | undefined): string {
  if (score === null || score === undefined || Number.isNaN(score)) {
    return `rgb(${NEUTRAL[0]}, ${NEUTRAL[1]}, ${NEUTRAL[2]})`;
  }
  const s = Math.max(0, Math.min(100, score));
  if (s <= STOPS[0].score) return rgb(STOPS[0].rgb);
  if (s >= STOPS[STOPS.length - 1].score) return rgb(STOPS[STOPS.length - 1].rgb);
  for (let i = 0; i < STOPS.length - 1; i++) {
    const lo = STOPS[i];
    const hi = STOPS[i + 1];
    if (s >= lo.score && s <= hi.score) {
      const t = (s - lo.score) / (hi.score - lo.score);
      return rgb([lerp(lo.rgb[0], hi.rgb[0], t), lerp(lo.rgb[1], hi.rgb[1], t), lerp(lo.rgb[2], hi.rgb[2], t)]);
    }
  }
  return rgb(STOPS[STOPS.length - 1].rgb);
}

function rgb(c: [number, number, number]): string {
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

/** Short word for the band a score falls in (used in tooltips/legend). */
export function bandLabel(score: number | null | undefined): string {
  if (score === null || score === undefined || Number.isNaN(score)) return "no evidence yet";
  if (score < 55) return "unlikely";
  if (score < 75) return "maybe";
  return "most likely";
}

/**
 * Discrete likelihood band for the cosmic-dark heatmap: grey (unlikely) · blue (maybe) ·
 * green (most likely) · "none" when there's no evidence. The bar HEIGHT carries the exact
 * score; the band carries the class — same thresholds as bandLabel.
 */
export function heatmapBand(score: number | null | undefined): "green" | "blue" | "grey" | "none" {
  if (score === null || score === undefined || Number.isNaN(score)) return "none";
  if (score < 55) return "grey";
  if (score < 75) return "blue";
  return "green";
}

/** CSS background for a heatmap bar fill, banded (vertical gradient blue/green, flat grey). */
export function bandGradient(score: number | null | undefined): string {
  const b = heatmapBand(score);
  if (b === "green") return "linear-gradient(180deg, var(--g2), var(--g1))";
  if (b === "blue") return "linear-gradient(180deg, var(--b2), var(--b1))";
  if (b === "grey") return "var(--n1)";
  return "var(--n0)";
}

// ── Time-label arithmetic (shared by the heatmap levels) ────────────────────────────────────

/** "HH:MM:SS" (or "HH:MM", or a date-qualified "YYYY-MM-DDTHH:MM:SS") → seconds since midnight. */
export function secondsOfLabel(label: string): number {
  // A multi-day candidate key carries an explicit day ("2024-01-02T05:30:00"); the heatmap's
  // time-of-day arithmetic wants only the clock part, so drop any date prefix first.
  const timePart = String(label).includes("T") ? String(label).split("T").pop()! : String(label);
  const [hh = 0, mm = 0, ss = 0] = timePart.split(":").map(Number);
  return hh * 3600 + mm * 60 + ss;
}

/** The date portion of a date-qualified candidate key ("YYYY-MM-DDTHH:MM:SS"), or null if undated. */
export function dateOfLabel(label: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})[T ]/.exec(String(label).trim());
  return m ? m[1] : null;
}

/** Seconds since midnight → "HH:MM:SS" (wraps a full day). */
export function labelOfSeconds(total: number): string {
  const t = ((Math.round(total) % 86400) + 86400) % 86400;
  const hh = Math.floor(t / 3600);
  const mm = Math.floor((t % 3600) / 60);
  const ss = t % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

/** Forward width in seconds from start→end, wrapping past midnight (always ≥ 0). */
export function forwardWidthSeconds(startLabel: string, endLabel: string): number {
  let w = secondsOfLabel(endLabel) - secondsOfLabel(startLabel);
  if (w < 0) w += 86400;
  return w;
}

/**
 * Calendar dates for a SUB-window carved out of a (possibly overnight) base window.
 *
 * Narrowing an overnight window (e.g. 21:00→01:00 spanning D→D+1) to a band that does NOT cross
 * midnight must also renormalise the dates, or the stale end_date leaves a ~25h "multi-day"
 * window (span math counts D→D+1 PLUS the clock width) that pins the engine to coarse scans.
 *
 * Rules (all clock arithmetic is FORWARD from the base window's start):
 *   - new start_date = base start_date, PLUS one day when the sub-window's start lies past
 *     midnight relative to the base start (its forward offset from the base start crosses 86400 —
 *     e.g. 00:10 inside a 21:00→01:00 window is the day AFTER the base start date);
 *   - new end_date = start_date, PLUS one day only when the sub-window ITSELF crosses midnight
 *     (end clock at or before start clock — same rule as commit()'s crossesMidnight).
 *
 * Only meaningful for single-day/overnight bases (≤24h). A genuine multi-day window commits via
 * an explicit per-day date (the bestDay branch) and must not be renormalised through this.
 */
/**
 * Where the empty-stack zoom-out lands: the profile's BASE search window — `base_window` when a
 * commit captured one, else the birth range the intake (or a later "Edit window" correction)
 * recorded. NEVER a hardcoded full day: that used to blow a narrower base (an intake ± window, or
 * an "Edit window" overnight correction like 14:00→02:00) back up to 00:00–23:59 AND persist it,
 * silently destroying the user's window on the first zoom-out past the commit level.
 */
export function zoomOutTarget(profile: any): {
  start: string;
  end: string;
  start_date: string | null;
  end_date: string | null;
} {
  const base = profile?.base_window;
  const birthDate: string | null = profile?.birth?.date ?? null;
  if (base?.start && base?.end) {
    return {
      start: base.start,
      end: base.end,
      start_date: base.start_date ?? birthDate,
      end_date: base.end_date ?? birthDate,
    };
  }
  const birth = profile?.birth ?? {};
  const start: string = birth.range_start || "00:00:00";
  const end: string = birth.range_end || "23:59:59";
  let end_date: string | null = birth.range_end_date || null;
  if (!end_date && birthDate) {
    // No explicit end date: an overnight range (end at/before start) ends the day after birth.
    end_date =
      secondsOfLabel(end) <= secondsOfLabel(start)
        ? formatISODate(addDays(parseISODate(birthDate), 1))
        : birthDate;
  }
  return { start, end, start_date: birthDate, end_date };
}

export function subWindowDates(
  baseStartDate: string,
  baseStartLabel: string,
  subStartLabel: string,
  subEndLabel: string,
): { start_date: string; end_date: string } {
  const DAY = 86400;
  const baseStart = secondsOfLabel(baseStartLabel);
  const subStart = secondsOfLabel(subStartLabel);
  const subEnd = secondsOfLabel(subEndLabel);
  const offset = (((subStart - baseStart) % DAY) + DAY) % DAY;
  const dayShift = baseStart + offset >= DAY ? 1 : 0;
  const start = addDays(parseISODate(baseStartDate), dayShift);
  const crossesMidnight = subEnd <= subStart;
  return {
    start_date: formatISODate(start),
    end_date: formatISODate(crossesMidnight ? addDays(start, 1) : start),
  };
}
